import { useState, useEffect } from 'react';
import { CalendarDays, Clock, Download, FileText, Search, Users } from 'lucide-react';
import apiClient from '../../services/api';
import { DatePicker } from '../ui/DatePicker';

interface Staff {
    id: string;
    name: string;
    username: string;
    email: string;
    role: { name: string };
}

interface Timesheet {
    id: string;
    shiftDate: string;
    startTime: string;
    endTime: string;
    totalHours: number;
    user: { id: string; name: string };
}

type PayrollType = 'AGENCY' | 'EAGLE';
type ExportState = 'AGENCY_PDF' | 'AGENCY_XLSX' | 'EAGLE_PDF' | 'EAGLE_XLSX' | null;
const IAN_EAGLE_PAYROLL_START = '2026-06-01';

const PayrollView = () => {
    const [staff, setStaff] = useState<Staff[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [search, setSearch] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
    const [previewPayrollType, setPreviewPayrollType] = useState<PayrollType>('AGENCY');
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState<ExportState>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        apiClient.get('/payroll/staff')
            .then(response => setStaff(response.data.data || []))
            .catch(() => setError('Failed to load payroll staff list'));
    }, []);

    useEffect(() => {
        if (!selectedIds.length || !startDate || !endDate) {
            setTimesheets([]);
            return;
        }
        setLoading(true);
        setError('');
        apiClient.get('/payroll/preview', {
            params: { userIds: selectedIds.join(','), startDate, endDate, status: 'APPROVED', payrollType: previewPayrollType },
        })
            .then(response => setTimesheets(response.data.data || []))
            .catch((err) => setError(err.response?.data?.error?.message || 'Failed to load payroll preview'))
            .finally(() => setLoading(false));
    }, [selectedIds, startDate, endDate, previewPayrollType]);

    const filteredStaff = staff.filter(person => {
        const query = search.toLowerCase();
        return person.name.toLowerCase().includes(query)
            || person.email.toLowerCase().includes(query)
            || person.role.name.toLowerCase().includes(query);
    });

    const toggleStaff = (id: string) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
    };

    const totalHours = timesheets.reduce((sum, item) => sum + item.totalHours, 0);
    const canExport = selectedIds.length > 0 && startDate && endDate && timesheets.length > 0;
    const isExporting = Boolean(exporting);
    const ianSelected = staff.some(person => selectedIds.includes(person.id) && person.username === 'ianm');
    const ianEagleCutoffApplies = previewPayrollType === 'EAGLE'
        && ianSelected
        && startDate < IAN_EAGLE_PAYROLL_START
        && endDate >= IAN_EAGLE_PAYROLL_START;

    const filePrefix = (payrollType: PayrollType, format: 'pdf' | 'xlsx') => {
        if (format === 'pdf') return payrollType === 'EAGLE' ? 'eagle-payroll-timesheet' : 'apl-agency-timesheet';
        return payrollType === 'EAGLE' ? 'eagle-payroll-hours' : 'agency-payroll-hours';
    };

    const exportWorkbook = async (payrollType: PayrollType) => {
        if (!canExport) return;
        setExporting(`${payrollType}_XLSX`);
        setError('');
        try {
            const response = await apiClient.post('/payroll/export', {
                userIds: selectedIds,
                startDate,
                endDate,
                status: 'APPROVED',
                payrollType,
            }, { responseType: 'blob' });

            const blobUrl = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `${filePrefix(payrollType, 'xlsx')}-${startDate}-to-${endDate}.xlsx`;
            link.click();
            URL.revokeObjectURL(blobUrl);
        } catch (err: any) {
            setError(err.response?.data?.error?.message || 'Failed to export payroll workbook');
        } finally {
            setExporting(null);
        }
    };

    const exportPdf = async (payrollType: PayrollType) => {
        if (!canExport) return;
        setExporting(`${payrollType}_PDF`);
        setError('');
        try {
            const response = await apiClient.post('/payroll/export/pdf', {
                userIds: selectedIds,
                startDate,
                endDate,
                status: 'APPROVED',
                payrollType,
            }, { responseType: 'blob' });

            const blobUrl = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `${filePrefix(payrollType, 'pdf')}-${startDate}-to-${endDate}.pdf`;
            link.click();
            URL.revokeObjectURL(blobUrl);
        } catch (err: any) {
            setError(err.response?.data?.error?.message || 'Failed to export payroll PDF');
        } finally {
            setExporting(null);
        }
    };

    const renderPayrollAction = (payrollType: PayrollType) => {
        const isAgencyPayroll = payrollType === 'AGENCY';
        const title = isAgencyPayroll ? 'Agency Payroll' : 'Eagle Payroll';
        const helper = canExport
            ? `Create ${title.toLowerCase()} files for the selected staff.`
            : 'Select staff, date range, and approved hours to export.';

        return (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="font-semibold text-slate-800">{title}</h3>
                        <p className="text-sm text-slate-500 mt-1">{helper}</p>
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                        Format
                    </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                    <button
                        onClick={() => exportPdf(payrollType)}
                        disabled={!canExport || isExporting}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FileText className="w-4 h-4" />
                        {exporting === `${payrollType}_PDF` ? 'Creating PDF' : 'Export PDF'}
                    </button>
                    <button
                        onClick={() => exportWorkbook(payrollType)}
                        disabled={!canExport || isExporting}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Download className="w-4 h-4" />
                        {exporting === `${payrollType}_XLSX` ? 'Creating Excel' : 'Export Excel'}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-5">
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Payroll Export</h1>
                <p className="text-slate-500 mt-1">Export approved hours for selected staff.</p>
            </div>

            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>}

            <div className="grid lg:grid-cols-[360px_1fr] gap-5">
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                    <div className="p-4 border-b border-slate-200">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Staff</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                            <input value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none" placeholder="Search staff" />
                        </div>
                        <div className="mt-3 flex items-center justify-between text-sm">
                            <span className="font-semibold text-slate-700">{selectedIds.length} selected</span>
                            <button onClick={() => setSelectedIds(selectedIds.length === staff.length ? [] : staff.map(person => person.id))} className="text-blue-600 font-semibold">
                                {selectedIds.length === staff.length ? 'Clear all' : 'Select all'}
                            </button>
                        </div>
                    </div>
                    <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100">
                        {filteredStaff.map(person => (
                            <label key={person.id} className="flex items-start gap-3 p-3 hover:bg-slate-50 cursor-pointer">
                                <input type="checkbox" checked={selectedIds.includes(person.id)} onChange={() => toggleStaff(person.id)} className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                                <span className="min-w-0">
                                    <span className="block font-semibold text-slate-800 truncate">{person.name}</span>
                                    <span className="block text-xs text-slate-500 truncate">{person.role.name} · {person.email}</span>
                                </span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="space-y-5">
                    <div className="bg-white p-4 sm:p-5 rounded-lg shadow-sm border border-slate-200">
                        <div className="grid lg:grid-cols-[1fr_1fr_auto] gap-4 items-end">
                            <DatePicker label="Start Date" value={startDate} onChange={setStartDate} placeholder="Pick start date" />
                            <DatePicker label="End Date" value={endDate} onChange={setEndDate} min={startDate} placeholder="Pick end date" />
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Preview As</label>
                                <div className="inline-flex rounded-lg bg-slate-100 p-1">
                                    {(['AGENCY', 'EAGLE'] as PayrollType[]).map(type => (
                                        <button
                                            key={type}
                                            type="button"
                                            onClick={() => setPreviewPayrollType(type)}
                                            className={`px-3 py-2 rounded-md text-sm font-semibold transition ${previewPayrollType === type ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            {type === 'AGENCY' ? 'Agency' : 'Eagle'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {ianEagleCutoffApplies && (
                            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                Ian Miles is counted from 01/06/2026 for Eagle payroll. Earlier hours in this selected range are excluded because agency payroll was paid through 31/05/2026.
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex items-center gap-3">
                            <Users className="w-5 h-5 text-blue-600" />
                            <div>
                                <p className="text-xs font-semibold text-blue-600 uppercase">Staff</p>
                                <p className="text-2xl font-bold text-blue-700">{selectedIds.length}</p>
                            </div>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex items-center gap-3">
                            <CalendarDays className="w-5 h-5 text-slate-600" />
                            <div>
                                <p className="text-xs font-semibold text-slate-600 uppercase">Rows</p>
                                <p className="text-2xl font-bold text-slate-800">{timesheets.length}</p>
                            </div>
                        </div>
                        <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-100 flex items-center gap-3">
                            <Clock className="w-5 h-5 text-emerald-600" />
                            <div>
                                <p className="text-xs font-semibold text-emerald-600 uppercase">Hours</p>
                                <p className="text-2xl font-bold text-emerald-700">{totalHours.toFixed(2)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                        {renderPayrollAction('AGENCY')}
                        {renderPayrollAction('EAGLE')}
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <h3 className="font-semibold text-slate-800">Preview</h3>
                            <div className="flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
                                <span className="px-2 py-1 rounded-full bg-white border border-slate-200">{selectedIds.length} selected staff</span>
                                <span className="px-2 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700">{totalHours.toFixed(2)} approved hrs</span>
                            </div>
                        </div>

                        {loading ? (
                            <div className="p-10 text-center text-slate-500">Loading preview...</div>
                        ) : timesheets.length === 0 ? (
                            <div className="p-10 text-center text-slate-500">Select staff and date range to preview approved hours.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-200 text-sm">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-500">Staff</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-500">Date</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-500">Start</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-500">Finish</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-500">Hours</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200">
                                        {timesheets.map(ts => (
                                            <tr key={ts.id}>
                                                <td className="px-4 py-3 font-medium text-slate-800">{ts.user.name}</td>
                                                <td className="px-4 py-3 text-slate-600">{new Date(ts.shiftDate).toLocaleDateString()}</td>
                                                <td className="px-4 py-3 text-slate-600">{new Date(ts.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                                                <td className="px-4 py-3 text-slate-600">{new Date(ts.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                                                <td className="px-4 py-3 font-semibold text-slate-800">{ts.totalHours}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PayrollView;
